import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Code,
  Drawer,
  Group,
  ScrollArea,
  SegmentedControl,
  Skeleton,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";
import type { AnalyticsCredential, AnalyticsPlatform } from "../../../api.ts";
import { adminErrorMessage, adminGet, adminSend, AdminApiError } from "../../../api.ts";
import { AdminForm, AdminFormActions } from "../../../components/AdminForm.tsx";
import { notifyAdminError, notifyAdminSuccess } from "../../../lib/admin-notify.ts";
import { AdminTable } from "../../../components/AdminTable.tsx";
import { TablePagination } from "../../../components/TablePagination.tsx";
import { useClientPagination } from "../../../hooks/useClientPagination.ts";
import { botSectionHref } from "../../../lib/bot-nav.ts";
import { CredentialDeleteConfirm } from "./CredentialDeleteConfirm.tsx";
import { GoogleCredentialCard } from "./GoogleCredentialCard.tsx";
import { PlatformTutorialBody, platformTutorialTitle } from "./PlatformTutorials.tsx";
import { ANALYTICS_PLATFORM_SEGMENTS } from "./platform-segments.ts";

type CredPlatform = AnalyticsPlatform;

const CRED_PLATFORMS = new Set<string>(ANALYTICS_PLATFORM_SEGMENTS.map((p) => p.value));

interface CredentialForm {
  externalId: string;
  accessToken: string;
}

interface SharechatForm {
  advId: string;
}

interface TikTokAppForm {
  pixelCode: string;
  appId: string;
  secret: string;
}

function AuthStatusBadge(props: {
  readonly authStatus: AnalyticsCredential["authStatus"];
  readonly accessTokenExpiresAt: string | null;
}) {
  const { authStatus, accessTokenExpiresAt } = props;
  if (authStatus === null) return null;
  const colorMap = { pending: "gray", authorized: "green", expired: "red" } as const;
  const labelMap = { pending: "待授权", authorized: "已授权", expired: "已过期" } as const;
  return (
    <Stack gap={2}>
      <Badge color={colorMap[authStatus]} size="sm">
        {labelMap[authStatus]}
      </Badge>
      {authStatus === "authorized" && accessTokenExpiresAt !== null && (
        <Text size="xs" c="dimmed">
          Token 过期: {new Date(accessTokenExpiresAt).toLocaleString()}
        </Text>
      )}
    </Stack>
  );
}

function externalIdLabel(platform: Exclude<CredPlatform, "google">): string {
  if (platform === "facebook") return "Pixel / Dataset ID";
  if (platform === "tiktok") return "Pixel Code";
  return "App Name";
}

export function PlatformCredentialsTab(props: { readonly botId: string }) {
  const { botId } = props;
  const qc = useQueryClient();
  const navigate = useNavigate();
  // splat：credentials 或 credentials/sharechat（父路由 bots/:id/*）
  const { "*": splat = "" } = useParams<{ "*": string }>();
  const splatParts = splat.split("/").filter(Boolean);
  // credentials / facebook 时 parts = [credentials, facebook]
  const platformFromPath =
    splatParts[0] === "credentials" &&
    splatParts[1] !== undefined &&
    CRED_PLATFORMS.has(splatParts[1])
      ? (splatParts[1] as CredPlatform)
      : null;

  const [platform, setPlatform] = useState<CredPlatform>(platformFromPath ?? "facebook");
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editRow, setEditRow] = useState<AnalyticsCredential | null>(null);
  const connectingRef = useRef<string | null>(null);

  // URL -> 状态；仅 /credentials 时补默认平台段
  useEffect(() => {
    if (splatParts[0] === "credentials" && platformFromPath === null) {
      void navigate(botSectionHref(botId, `credentials/${platform}`), { replace: true });
      return;
    }
    if (platformFromPath !== null && platformFromPath !== platform) {
      setPlatform(platformFromPath);
    }
  }, [platformFromPath, platform, splatParts, botId, navigate]);

  const fbForm = useForm<CredentialForm>({
    initialValues: { externalId: "", accessToken: "" },
    validate: {
      externalId: (v) => (v.trim().length === 0 ? "请填写 Pixel ID" : null),
      accessToken: (v) => (v.trim().length === 0 ? "请填写 Access Token" : null),
    },
  });

  const sharechatForm = useForm<SharechatForm>({
    initialValues: { advId: "" },
    validate: {
      advId: (v) => (v.trim().length < 2 ? "请填写 Event Manager 的 App Name（ADVID）" : null),
    },
  });

  const tiktokForm = useForm<TikTokAppForm>({
    initialValues: { pixelCode: "", appId: "", secret: "" },
    validate: {
      pixelCode: (v) => (v.trim().length === 0 ? "请填写 Pixel Code" : null),
      appId: (v) => (v.trim().length === 0 ? "请填写 TikTok App ID" : null),
      secret: (v) => (v.trim().length === 0 ? "请填写 App Secret" : null),
    },
  });

  const list = useQuery({
    queryKey: ["bot", botId, "analytics-credentials"],
    queryFn: () => adminGet<{ items: AnalyticsCredential[] }>(`/analytics/${botId}/credentials`),
    refetchInterval: 60_000,
  });

  const items = list.data?.items ?? [];
  const countBy = useMemo(() => {
    const m: Record<CredPlatform, number> = {
      facebook: 0,
      tiktok: 0,
      google: 0,
      sharechat: 0,
    };
    for (const r of items) {
      if (r.platform in m) m[r.platform] += 1;
    }
    return m;
  }, [items]);

  const [enabledFilter, setEnabledFilter] = useState<"all" | "on" | "off">("all");
  const [search, setSearch] = useState("");
  const [remarkEdit, setRemarkEdit] = useState<AnalyticsCredential | null>(null);
  const [remarkDraft, setRemarkDraft] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<AnalyticsCredential | null>(null);
  const [deleteStep, setDeleteStep] = useState<1 | 2>(1);

  const platformItems = useMemo(
    () => items.filter((r) => r.platform === platform),
    [items, platform],
  );
  const keyword = search.trim().toLowerCase();
  const filteredItems = useMemo(() => {
    if (platform === "google") return [];
    return platformItems.filter((r) => {
      if (enabledFilter === "on" && !r.isEnabled) return false;
      if (enabledFilter === "off" && r.isEnabled) return false;
      if (keyword === "") return true;
      return (
        r.externalId.toLowerCase().includes(keyword) ||
        (r.remark ?? "").toLowerCase().includes(keyword)
      );
    });
  }, [platformItems, enabledFilter, keyword, platform]);

  const resetKey = `${platform}|${enabledFilter}|${keyword}`;
  const { pageItems, page, setPage, pageSize, total, totalPages } = useClientPagination(
    filteredItems,
    resetKey,
  );

  const upsertFb = useMutation({
    mutationFn: (v: CredentialForm) =>
      adminSend("PUT", `/analytics/${botId}/credentials`, {
        platform: "facebook",
        externalId: v.externalId.trim(),
        accessToken: v.accessToken.trim(),
      }),
    onSuccess: () => {
      notifyAdminSuccess(
        "Facebook 凭据已保存。保存成功后可用列表「测试」验证 CAPI 写入。",
        "保存成功",
      );
      setFormOpen(false);
      setEditRow(null);
      fbForm.reset();
      void qc.invalidateQueries({ queryKey: ["bot", botId, "analytics-credentials"] });
    },
    onError: (e) => notifyAdminError(e, "保存失败"),
  });

  const upsertSharechat = useMutation({
    mutationFn: (v: SharechatForm) =>
      adminSend("PUT", `/analytics/${botId}/credentials/sharechat`, {
        advId: v.advId.trim(),
        isEnabled: true,
      }),
    onSuccess: () => {
      notifyAdminSuccess("ShareChat 凭据已保存", "保存成功");
      setFormOpen(false);
      sharechatForm.reset();
      void qc.invalidateQueries({ queryKey: ["bot", botId, "analytics-credentials"] });
    },
    onError: (e) => notifyAdminError(e, "保存失败"),
  });

  const saveTiktokApp = useMutation({
    mutationFn: (v: TikTokAppForm) =>
      adminSend("PUT", `/analytics/${botId}/credentials/tiktok-app`, {
        pixelCode: v.pixelCode.trim(),
        appId: v.appId.trim(),
        secret: v.secret.trim(),
      }),
    onSuccess: () => {
      notifyAdminSuccess(
        "已保存 TikTok 应用凭据。请在列表点「连接 TikTok」完成 OAuth。",
        "保存成功",
      );
      setFormOpen(false);
      tiktokForm.reset();
      void qc.invalidateQueries({ queryKey: ["bot", botId, "analytics-credentials"] });
    },
    onError: (e) => notifyAdminError(e, "保存失败"),
  });

  const toggle = useMutation({
    mutationFn: (args: { id: string; isEnabled: boolean }) =>
      adminSend<{ ok: boolean; requeued?: number }>(
        "PATCH",
        `/analytics/${botId}/credentials/${args.id}`,
        {
          isEnabled: args.isEnabled,
          // 重新启用时自动把 pin 到该凭据的 failed 转化重入队
          ...(args.isEnabled ? { requeueFailed: true } : {}),
        },
      ),
    onSuccess: (data, vars) => {
      void qc.invalidateQueries({ queryKey: ["bot", botId, "analytics-credentials"] });
      void qc.invalidateQueries({ queryKey: ["bot", botId, "analytics-conversions"] });
      if (vars.isEnabled && (data.requeued ?? 0) > 0) {
        notifyAdminSuccess(`凭据已启用，并重入队 ${data.requeued} 条失败转化`);
      } else if (vars.isEnabled) {
        notifyAdminSuccess("凭据已启用");
      }
    },
    onError: (e) => notifyAdminError(e, "更新失败"),
  });

  const testCred = useMutation({
    mutationFn: (credId: string) =>
      adminSend<{ ok: boolean; error?: string; note?: string }>(
        "POST",
        `/analytics/${botId}/credentials/${credId}/test`,
      ),
    onSuccess: (data) => {
      if (data.ok) {
        notifyAdminSuccess(data.note !== undefined ? data.note : "凭据可用", "测试通过");
      } else {
        notifications.show({
          color: "red",
          title: "测试未通过",
          message: data.error ?? "未知错误",
          autoClose: 12_000,
        });
      }
    },
    onError: (e) => notifyAdminError(e, "测试失败"),
  });

  const deleteCred = useMutation({
    mutationFn: (credId: string) =>
      adminSend("DELETE", `/analytics/${botId}/credentials/${credId}`),
    onSuccess: () => {
      notifyAdminSuccess("凭据已删除。已 pin 的广告需重新绑定后才会再上报。");
      setDeleteTarget(null);
      setDeleteStep(1);
      void qc.invalidateQueries({ queryKey: ["bot", botId, "analytics-credentials"] });
    },
    onError: (e) => notifyAdminError(e, "删除失败"),
  });

  const saveRemark = useMutation({
    mutationFn: (args: { id: string; remark: string }) =>
      adminSend("PATCH", `/analytics/${botId}/credentials/${args.id}`, {
        remark: args.remark.trim() === "" ? null : args.remark.trim().slice(0, 64),
      }),
    onSuccess: () => {
      notifyAdminSuccess("备注已保存");
      setRemarkEdit(null);
      void qc.invalidateQueries({ queryKey: ["bot", botId, "analytics-credentials"] });
    },
    onError: (e) => notifyAdminError(e, "保存失败"),
  });

  const handleConnectTikTok = async (credId: string) => {
    if (connectingRef.current === credId) return;
    connectingRef.current = credId;
    try {
      const { authUrl } = await adminGet<{ authUrl: string }>(
        `/analytics/${botId}/tiktok/auth-url`,
      );
      sessionStorage.setItem("tiktok_oauth_botId", botId);
      window.location.href = authUrl;
    } catch (e) {
      connectingRef.current = null;
      notifyAdminError(e, "无法打开 TikTok 授权");
    }
  };

  const openAdd = () => {
    setEditRow(null);
    fbForm.reset();
    tiktokForm.reset();
    sharechatForm.reset();
    upsertFb.reset();
    upsertSharechat.reset();
    saveTiktokApp.reset();
    setFormOpen(true);
  };

  const openEdit = (row: AnalyticsCredential) => {
    if (row.platform !== "facebook" && row.platform !== "tiktok") return;
    setEditRow(row);
    upsertFb.reset();
    if (row.platform === "facebook") {
      fbForm.setValues({ externalId: row.externalId, accessToken: "" });
    }
    setFormOpen(true);
  };

  const switchPlatform = (p: CredPlatform) => {
    setPlatform(p);
    setSearch("");
    setEnabledFilter("all");
    setFormOpen(false);
    setEditRow(null);
    void navigate(botSectionHref(botId, `credentials/${p}`), { replace: true });
  };

  const rows = pageItems.map((row) => (
    <Table.Tr key={row.id}>
      <Table.Td>
        <Text size="sm" ff="monospace">
          {row.externalId}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" lineClamp={1} title={row.remark ?? undefined}>
          {row.remark !== null && row.remark !== "" ? row.remark : "无"}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="xs" c="dimmed" ff="monospace">
          {row.platform === "sharechat" ? "S2S 无 token" : row.accessTokenMasked}
        </Text>
      </Table.Td>
      <Table.Td>
        {row.platform === "tiktok" ? (
          <AuthStatusBadge
            authStatus={row.authStatus}
            accessTokenExpiresAt={row.accessTokenExpiresAt}
          />
        ) : row.platform === "sharechat" ? (
          <Badge color="gray" size="sm" variant="light">
            S2S
          </Badge>
        ) : (
          <Badge color="gray" size="sm" variant="light">
            token
          </Badge>
        )}
      </Table.Td>
      <Table.Td>
        <Switch
          checked={row.isEnabled}
          size="sm"
          onChange={(e) => toggle.mutate({ id: row.id, isEnabled: e.currentTarget.checked })}
        />
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          {row.platform === "facebook" ? (
            <Button size="xs" variant="outline" onClick={() => openEdit(row)}>
              编辑
            </Button>
          ) : null}
          <Button
            size="xs"
            variant="light"
            onClick={() => {
              setRemarkEdit(row);
              setRemarkDraft(row.remark ?? "");
            }}
          >
            备注
          </Button>
          <Button
            size="xs"
            variant="outline"
            color="teal"
            loading={testCred.isPending && testCred.variables === row.id}
            title={
              row.platform === "facebook"
                ? "向 Pixel 发送 1 条 CAPI 测试事件，验证 token 能否上报"
                : "测试该凭据是否可用"
            }
            onClick={() => testCred.mutate(row.id)}
          >
            测试
          </Button>
          {row.platform === "tiktok" ? (
            <Button
              size="xs"
              variant="outline"
              color="violet"
              onClick={() => void handleConnectTikTok(row.id)}
            >
              {row.authStatus === "authorized" ? "重新授权" : "连接 TikTok"}
            </Button>
          ) : null}
          <Button
            size="xs"
            variant="outline"
            color="red"
            onClick={() => {
              setDeleteTarget(row);
              setDeleteStep(1);
            }}
          >
            删除
          </Button>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <>
      <Stack gap="md">
        <Group justify="space-between" align="flex-end" wrap="wrap" gap="sm">
          <Stack gap={4}>
            <Text fw={600} size="lg">
              平台凭据
            </Text>
            <Text size="xs" c="dimmed">
              列表为主；教程与添加在右侧抽屉。投放 URL
              在「频道/群聊」复制。保存失败请看红条完整原因；若列表已有该
              Pixel，先刷新再决定是否重填。
            </Text>
          </Stack>
          <Group gap="xs">
            <Button variant="light" onClick={() => setTutorialOpen(true)}>
              接入教程
            </Button>
            {platform !== "google" ? (
              <Button onClick={openAdd}>
                {platform === "facebook"
                  ? "添加 Facebook"
                  : platform === "tiktok"
                    ? "添加 TikTok"
                    : "添加 ShareChat"}
              </Button>
            ) : null}
          </Group>
        </Group>

        <SegmentedControl
          fullWidth
          value={platform}
          onChange={(v) => switchPlatform(v as CredPlatform)}
          data={ANALYTICS_PLATFORM_SEGMENTS.map((p) => ({
            value: p.value,
            label: `${p.label} (${countBy[p.value]})`,
          }))}
        />

        {platform === "google" ? (
          <GoogleCredentialCard
            botId={botId}
            embedded
            onOpenTutorial={() => setTutorialOpen(true)}
          />
        ) : (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Text size="xs" c="dimmed" mb="sm">
              {platform === "sharechat"
                ? "只填 Event Manager 的 App Name（= 文档 ADVID）。无 token。App Name 全库唯一（跨 Bot，大小写不敏感）；建名无成本，禁止与其它广告户撞名。投放 URL 带 advid= 可 pin。"
                : platform === "facebook"
                  ? "同一 Bot 下 Pixel ID 不可重复；已存在请点「编辑」换 token，不要再点添加。Token 加密存储。「测试」发 1 条 CAPI 写入，不是读数据集权限。"
                  : `唯一键 (bot, ${platform}, ${externalIdLabel(platform)})。Token 加密存储。`}
            </Text>
            {list.isLoading ? (
              <Stack gap="xs">
                {Array.from({ length: 2 }).map((_, i) => (
                  <Skeleton key={i} h={40} radius="sm" />
                ))}
              </Stack>
            ) : platformItems.length === 0 ? (
              <Center py="xl">
                <Stack align="center" gap="sm">
                  <Text size="md" fw={600} c="dimmed">
                    暂无 {platform} 凭据
                  </Text>
                  <Group gap="xs">
                    <Button variant="light" onClick={() => setTutorialOpen(true)}>
                      先看教程
                    </Button>
                    <Button onClick={openAdd}>添加凭据</Button>
                  </Group>
                </Stack>
              </Center>
            ) : (
              <>
                <Group gap="sm" mb="sm" wrap="wrap">
                  <SegmentedControl
                    size="xs"
                    value={enabledFilter}
                    onChange={(v) => setEnabledFilter(v as "all" | "on" | "off")}
                    data={[
                      { label: "全部", value: "all" },
                      { label: "启用", value: "on" },
                      { label: "禁用", value: "off" },
                    ]}
                  />
                  <TextInput
                    size="xs"
                    placeholder={`${externalIdLabel(platform)} / 备注`}
                    value={search}
                    onChange={(e) => setSearch(e.currentTarget.value)}
                    style={{ minWidth: 200, flex: 1 }}
                  />
                </Group>
                <AdminTable minWidth={640}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{externalIdLabel(platform)}</Table.Th>
                      <Table.Th>备注</Table.Th>
                      <Table.Th>凭证</Table.Th>
                      <Table.Th>状态</Table.Th>
                      <Table.Th>启用</Table.Th>
                      <Table.Th>操作</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {pageItems.length === 0 ? (
                      <Table.Tr>
                        <Table.Td colSpan={6}>
                          <Text c="dimmed" size="sm" ta="center">
                            无匹配凭据
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ) : (
                      rows
                    )}
                  </Table.Tbody>
                </AdminTable>
                <TablePagination
                  page={page}
                  pageSize={pageSize}
                  total={total}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              </>
            )}
          </Card>
        )}
      </Stack>

      <Drawer
        opened={tutorialOpen}
        onClose={() => setTutorialOpen(false)}
        title={platformTutorialTitle(platform)}
        size="xl"
        position="right"
      >
        <ScrollArea h="calc(100vh - 80px)" type="auto" offsetScrollbars>
          <PlatformTutorialBody platform={platform} />
        </ScrollArea>
      </Drawer>

      <Drawer
        opened={formOpen && platform !== "google"}
        onClose={() => {
          setFormOpen(false);
          setEditRow(null);
        }}
        title={
          platform === "facebook"
            ? editRow !== null
              ? "编辑 Facebook 凭据"
              : "添加 Facebook 凭据"
            : platform === "tiktok"
              ? "添加 TikTok 凭据"
              : "添加 ShareChat 凭据"
        }
        size="lg"
        position="right"
      >
        {platform === "facebook" ? (
          <AdminForm onSubmit={fbForm.onSubmit((v) => upsertFb.mutate(v))}>
            <Alert color="blue" title="用该 Pixel 的 CAPI 访问令牌">
              <Text size="xs">
                打开 Events Manager -&gt; 该数据集 -&gt; 设置 -&gt; Conversions API -&gt;
                生成访问令牌。Business Manager 分享数据集不会让旧 token 自动获得写入权限。同一 Pixel
                已在列表中时请点「编辑」，不要再添加。
              </Text>
            </Alert>
            {upsertFb.isError ? (
              <Alert color="red" title="保存失败">
                <Text size="xs" className="admin-error-text">
                  {adminErrorMessage(upsertFb.error)}
                  {upsertFb.error instanceof AdminApiError &&
                  upsertFb.error.code === "INTERNAL_ERROR"
                    ? "\n若列表已出现该 Pixel，说明上次已保存成功，请关闭抽屉并刷新，勿重复提交。"
                    : ""}
                </Text>
              </Alert>
            ) : null}
            <TextInput
              label="Pixel ID / Dataset ID"
              description={
                editRow !== null
                  ? "编辑时不可改 Pixel ID。要换 Pixel 请新增一条凭据。"
                  : "事件管理工具概览里的数字 ID。同一 Bot 下不可重复。"
              }
              required
              disabled={editRow !== null}
              {...fbForm.getInputProps("externalId")}
            />
            <TextInput
              label="Access Token"
              type="password"
              placeholder={editRow !== null ? "必须重新粘贴完整 token" : "粘贴完整 token"}
              description="保存后加密存储，列表只显示掩码。保存成功不代表 token 能上报，请再用「测试」。"
              required
              {...fbForm.getInputProps("accessToken")}
            />
            <AdminFormActions>
              <Button variant="default" type="button" onClick={() => setFormOpen(false)}>
                取消
              </Button>
              <Button type="submit" loading={upsertFb.isPending}>
                保存
              </Button>
            </AdminFormActions>
          </AdminForm>
        ) : null}
        {platform === "tiktok" ? (
          <AdminForm onSubmit={tiktokForm.onSubmit((v) => saveTiktokApp.mutate(v))}>
            {saveTiktokApp.isError ? (
              <Alert color="red" title="保存失败">
                <Text size="xs">{adminErrorMessage(saveTiktokApp.error)}</Text>
              </Alert>
            ) : null}
            <TextInput label="Pixel Code" required {...tiktokForm.getInputProps("pixelCode")} />
            <TextInput label="TikTok App ID" required {...tiktokForm.getInputProps("appId")} />
            <TextInput
              label="App Secret"
              type="password"
              required
              {...tiktokForm.getInputProps("secret")}
            />
            <Text size="sm" c="dimmed">
              保存后在列表点「连接 TikTok」完成 OAuth。未授权前「测试」会提示去授权，不能代替
              OAuth。
            </Text>
            <AdminFormActions>
              <Button variant="default" type="button" onClick={() => setFormOpen(false)}>
                取消
              </Button>
              <Button type="submit" loading={saveTiktokApp.isPending}>
                保存
              </Button>
            </AdminFormActions>
          </AdminForm>
        ) : null}
        {platform === "sharechat" ? (
          <AdminForm onSubmit={sharechatForm.onSubmit((v) => upsertSharechat.mutate(v))}>
            {upsertSharechat.isError ? (
              <Alert color="red" title="保存失败">
                <Text size="xs" className="admin-error-text">
                  {adminErrorMessage(upsertSharechat.error)}
                </Text>
              </Alert>
            ) : null}
            <TextInput
              label="App Name（ADVID）"
              description="Event Manager 向导第 1 步的 App Name。全库唯一（跨 Bot，大小写不敏感）；建名无成本，请用 Brand_Product 等不撞名写法。不是 Advertiser Id / Website Pixel Id。无 token。"
              placeholder="如 BrandA_MipTg（与 Event Manager 一致且全局不重复）"
              required
              {...sharechatForm.getInputProps("advId")}
            />
            <Stack gap={4}>
              <Text size="sm" fw={500}>
                投放 URL 宏（填 ShareChat 广告 CTA；完整链接在「频道/群聊」复制）
              </Text>
              <Code
                block
                style={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                  overflowWrap: "anywhere",
                  fontSize: 11,
                  maxWidth: "100%",
                }}
              >
                {
                  "https://tg.deioio.com/s/<chatToken>?ad_id=%%AD_ID%%&clickId=%%DELIVERY_IDENTIFIER%%&campaignname=%%CAMPAIGN_NAME%%&gaid=%%ADVERTISER_IDENTIFIER%%&userId=%%PUBLISHER_IDENTIFIER%%&p=sharechat&advid=<AppName>"
                }
              </Code>
              <Text size="xs" c="dimmed">
                保存后 <Code>advid=</Code> 会替换为上面填的 App Name。详见「接入教程」。
              </Text>
            </Stack>
            <AdminFormActions>
              <Button variant="default" type="button" onClick={() => setFormOpen(false)}>
                取消
              </Button>
              <Button type="submit" loading={upsertSharechat.isPending}>
                保存
              </Button>
            </AdminFormActions>
          </AdminForm>
        ) : null}
      </Drawer>

      <Drawer
        opened={remarkEdit !== null}
        onClose={() => setRemarkEdit(null)}
        title="投放备注"
        size="sm"
        position="right"
      >
        <AdminForm
          onSubmit={(e) => {
            e.preventDefault();
            if (remarkEdit === null) return;
            saveRemark.mutate({ id: remarkEdit.id, remark: remarkDraft });
          }}
        >
          <Text size="sm" c="dimmed" mb="sm">
            {remarkEdit !== null ? `${remarkEdit.platform} · ${remarkEdit.externalId}` : ""}
          </Text>
          <TextInput
            label="备注（投放名称）"
            maxLength={64}
            value={remarkDraft}
            onChange={(e) => setRemarkDraft(e.currentTarget.value)}
          />
          <AdminFormActions>
            <Button variant="default" type="button" onClick={() => setRemarkEdit(null)}>
              取消
            </Button>
            <Button type="submit" loading={saveRemark.isPending}>
              保存
            </Button>
          </AdminFormActions>
        </AdminForm>
      </Drawer>

      <CredentialDeleteConfirm
        opened={deleteTarget !== null}
        step={deleteStep}
        summary={
          deleteTarget !== null
            ? `${deleteTarget.platform} · ${deleteTarget.externalId}${deleteTarget.remark !== null && deleteTarget.remark !== "" ? ` · ${deleteTarget.remark}` : ""}`
            : ""
        }
        loading={deleteCred.isPending}
        onClose={() => {
          if (deleteCred.isPending) return;
          setDeleteTarget(null);
          setDeleteStep(1);
        }}
        onContinue={() => setDeleteStep(2)}
        onConfirmDelete={() => {
          if (deleteTarget === null) return;
          deleteCred.mutate(deleteTarget.id);
        }}
      />
    </>
  );
}
